import { currentLoggedUserVar } from '@/shared/api';
import type { AccountSearchScope, QuickPatientSearchResult } from '@/shared/api/graphql/account';
import { ROUTES } from '@/shared/config';
import { useAiSearchQuestionIdeas } from '@/shared/lib/hooks/useAiSearchQuestionIdeas';
import { useFeatureFlag } from '@/shared/lib/hooks/useFeatureFlag';
import useNotifications from '@/shared/lib/hooks/useNotifications';
import { usePopover } from '@/shared/lib/hooks/usePopover';
import useShowMedicalServices from '@/shared/lib/hooks/useShowMedicalServices';
import { MedicalServices } from '@/shared/lib/types/admin';
import { FeatureFlag } from '@/shared/lib/types/relyingParty';
import { getAccountRedirectUrl } from '@/shared/lib/util/account';
import { getPatientIdFromPath } from '@/shared/lib/util/constants';
import { looksLikePrompt } from '@/shared/lib/util/smartSearch';
import { SideMenuContext, usePatientNavBarStore, useSwitchCompanyStore } from '@/shared/model';
import { useDockedPanelStore } from '@/shared/model/DockedPanelStore';
import { useRecentQuestionsStore } from '@/shared/model/RecentQuestionsStore';
import SwitchCompanyDialog from '@/shared/ui/components/Dialogs/Company/SwitchCompanyDialog';
import PatientNavBarControls from '@/shared/ui/components/PatientNavBarControls';
import type { LookupHandoff } from '@/shared/ui/components/PatientSearchBox';
import PatientSearchBox from '@/shared/ui/components/PatientSearchBox';
import { NotificationsPopover } from '@/shared/ui/components/Popovers';
import SecondaryMenu from '@/shared/ui/components/SecondaryMenu';
import StarredPatientsList from '@/shared/ui/components/StarredPatientsList';
import TruentityLogo from '@/shared/ui/components/TruentityLogo';
import { useReactiveVar } from '@apollo/client';
import AppsIcon from '@mui/icons-material/Apps';
import CloseIcon from '@mui/icons-material/Close';
import MenuIcon from '@mui/icons-material/Menu';
import MenuOpenIcon from '@mui/icons-material/MenuOpen';
import NotificationsIcon from '@mui/icons-material/Notifications';
import SearchIcon from '@mui/icons-material/Search';
import type { AppBarProps } from '@mui/material';
import { AppBar, Box, Collapse, IconButton, Stack, Toolbar, Tooltip, useMediaQuery } from '@mui/material';
import Badge from '@mui/material/Badge';
import { useTheme } from '@mui/material/styles';
import { useCallback, useContext, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

type Props = AppBarProps & {
  /** Enter on a question rather than a patient lookup. Without it, everything is a lookup. */
  onAskQuestion?: (question: string) => void;
};

const PATIENT_LOOKUP_FILTER_OPTIONS = [
  {
    label: 'First Name',
    value: 'firstName'
  },
  {
    label: 'Last Name',
    value: 'lastName'
  },
  {
    label: 'Phone Number',
    value: 'phoneNumber'
  },
  {
    label: 'Date of Birth',
    value: 'dateOfBirth'
  }
];

const switchPrompt = (patientName: string, fromCompany: string, toCompany: string): string =>
  `Patient ${patientName} is enrolled under ${toCompany}. You will need to sign out of ${fromCompany} ` +
  `and sign in to ${toCompany}. Do you wish to continue?`;

const Navbar = ({ onAskQuestion: askQuestion, ...props }: Props) => {
  // AI Search off (AdditionalFeatures.aiSearch): the box only looks patients up.
  const isAiSearchEnabled = useFeatureFlag(FeatureFlag.AI_SEARCH);
  const onAskQuestion = isAiSearchEnabled ? askQuestion : undefined;
  const { unreadNotificationCount } = useNotifications();
  const notificationPopover = usePopover();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const hasPatientBar = Boolean(getPatientIdFromPath(pathname));
  const theme = useTheme();
  const isSearchCollapsed = useMediaQuery(theme.breakpoints.down('md'));
  const { drawerOpen, drawerToggle, isMobileOrTablet } = useContext(SideMenuContext);
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const recentQuestions = useRecentQuestionsStore(state => state.questions);
  const getQuestionIdeas = useAiSearchQuestionIdeas();
  const dockedPanelWidth = useDockedPanelStore(state => state.dockedWidth);

  const isMTMViewable = useShowMedicalServices(MedicalServices.MTM);
  const isRPMViewable = useShowMedicalServices(MedicalServices.RPM);

  const isPatientNavPinned = usePatientNavBarStore(state => state.isPinned);
  const schedulePatientNavReveal = usePatientNavBarStore(state => state.scheduleReveal);

  const revealPatientNav = useCallback(
    (next: boolean) => {
      if (!hasPatientBar || isPatientNavPinned) return;
      schedulePatientNavReveal(next);
    },
    [hasPatientBar, isPatientNavPinned, schedulePatientNavReveal]
  );

  const currentUser = useReactiveVar(currentLoggedUserVar);
  const { setCurrentCompany, setNewCompany, setFallbackRoute } = useSwitchCompanyStore();
  const [rowInOtherCompany, setRowInOtherCompany] = useState<QuickPatientSearchResult | null>(null);

  const onLookupOptionClick = ({ filterOption, searchValue }: { filterOption: string; searchValue: string }) => {
    lookupPatient(searchValue, filterOption);
  };

  const lookupPatient = (search: string, filterOption = '', tab?: string) => {
    const tabParam = tab ? `&tab=${tab}` : '';
    navigate(`./patients/lookup?q=${encodeURIComponent(search)}&filterOption=${filterOption}&source=searchbar${tabParam}`);
    setMobileSearchOpen(false);
  };

  const patientUrl = (row: QuickPatientSearchResult) => {
    const service = isRPMViewable ? MedicalServices.RPM : isMTMViewable ? MedicalServices.MTM : MedicalServices.TCM;

    return getAccountRedirectUrl(row.truentityId, service, row.rpmStatus).replace(/^(\.\.\/)+/, '/');
  };

  const onSelectPatient = (row: QuickPatientSearchResult) => {
    if (row.relyingPartyId && row.relyingPartyId !== currentUser?.relyingParty?.id) {
      setRowInOtherCompany(row);
      return;
    }

    setMobileSearchOpen(false);
    navigate(patientUrl(row));
  };

  /**
   * Enter with no row highlighted: a question goes to AI Search, anything else to the existing
   * lookup page, which searches every field rather than the one the shape router picked for the
   * type-ahead.
   */
  const onSearchSubmit = (search: string) => {
    const term = search?.trim();
    if (!term) return;

    // TODO: replace the length heuristic with the backend's question classifier (other branch).
    // Same rule as the box's "Press Enter to ask this as a question" hint, so the two agree.
    if (onAskQuestion && looksLikePrompt(term)) {
      setMobileSearchOpen(false);
      onAskQuestion(term);
      return;
    }

    lookupPatient(term);
  };

  const confirmCompanySwitch = () => {
    const row = rowInOtherCompany;
    if (!row?.relyingPartyId) return;

    setNewCompany({ id: row.relyingPartyId, name: row.relyingPartyName ?? '' });
    setCurrentCompany({ id: currentUser?.relyingParty?.id ?? '', name: currentUser?.relyingParty?.name ?? '' });
    setFallbackRoute(patientUrl(row));

    setRowInOtherCompany(null);
    setMobileSearchOpen(false);
    navigate(ROUTES.SWITCH_COMPANY);
  };

  const onSeeAllResults = (handoff: LookupHandoff, scope: AccountSearchScope) => {
    lookupPatient(handoff.value, handoff.filterOption, scope === 'ALL_RELYING_PARTIES' ? 'global-lookup' : undefined);
  };

  const searchFieldOverrides = PATIENT_LOOKUP_FILTER_OPTIONS.map(option => ({
    label: option.label,
    onSelect: (term: string) => onLookupOptionClick({ filterOption: option.value, searchValue: term })
  }));

  const searchForm = (
    <Box
      sx={{
        flex: 1,
        /*
         * The breakpoints measure the screen, not the bar: with a panel docked the bar is the screen
         * less the panel, and 320px of search on a 1194px tablet pushed Starred and the user badges
         * under the panel. Docked, the search gives way first.
         */
        minWidth: dockedPanelWidth > 0 ? 160 : { sm: 120, md: 200, lg: 320 }
      }}
    >
      <PatientSearchBox
        onSelectPatient={onSelectPatient}
        onSeeAllResults={onSeeAllResults}
        onSubmitTerm={onSearchSubmit}
        fieldOverrides={searchFieldOverrides}
        // Only where questions can be asked: clicking one asks it again.
        savedPrompts={onAskQuestion ? recentQuestions : []}
        onSelectPrompt={onAskQuestion}
        getQuestionIdeas={onAskQuestion ? getQuestionIdeas : undefined}
        canAskQuestions={Boolean(onAskQuestion)}
        showCompanyTabs={Boolean(currentUser?.hasMultipleRelyingParties)}
      />
    </Box>
  );

  return (
    <>
      <AppBar
        {...props}
        position={'sticky'}
        elevation={0}
        sx={{
          // One above the nav, which now sits at drawer level: the header must stay on top of a
          // menu that starts beneath it, including when the mock-company banner pushes it down.
          zIndex: theme => theme.zIndex.drawer + 1,
          backgroundColor: 'common.white',
          backgroundImage: 'none',
          boxShadow: 'none',
          borderBottom: 'none'
        }}
      >
        {/*
          The whole header is a hover target for the auto-hiding patient links.
          
          They reveal on hovering the patient's own identity strip, which starts below the header
          -- so the band between the search field and that strip, and every control in it
          including the notifications bell, was dead space where the pointer sat and nothing
          happened. Reaching the top of the page is the gesture; the header is where it lands.
          
          A no-op when the links are pinned, and on every page that has no patient bar.
        */}
        <Toolbar
          onMouseEnter={() => revealPatientNav(true)}
          onMouseLeave={() => revealPatientNav(false)}
          sx={{ gap: { xs: 0.75, sm: 1, md: 1 }, padding: theme => theme.spacing(1) }}
        >
          <Stack
            direction={'row'}
            sx={{
              alignItems: 'center',
              flexShrink: 0
            }}
          >
            {/* On desktop the rail dock carries the toggle; behind a temporary drawer it is not
                reachable, so mobile/tablet gets it back in the header. */}
            {isMobileOrTablet && (
              <IconButton size={'medium'} edge={'start'} color={'inherit'} aria-label="Toggle navigation" onClick={drawerToggle}>
                {drawerOpen ? <MenuOpenIcon /> : <MenuIcon />}
              </IconButton>
            )}
            {/* The logo is the way home, which is why the rail no longer carries a Home button. */}
            <Tooltip title="Home">
              <Box
                component="button"
                type="button"
                aria-label="Home"
                onClick={() => navigate('/care-management/patients')}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  border: 'none',
                  background: 'none',
                  padding: 0,
                  cursor: 'pointer',
                  borderRadius: 1,
                  '&:focus-visible': { outline: theme => `2px solid ${theme.palette.primary.main}`, outlineOffset: 2 }
                }}
              >
                <TruentityLogo sx={{ maxWidth: { xs: '9rem', sm: '11rem', md: '13rem', lg: '19rem' }, margin: 0 }} />
              </Box>
            </Tooltip>
          </Stack>

          {isSearchCollapsed ? (
            <Box sx={{ flex: 1, display: 'flex', justifyContent: 'flex-end' }}>
              <IconButton color="inherit" onClick={() => setMobileSearchOpen(prev => !prev)}>
                <SearchIcon />
              </IconButton>
            </Box>
          ) : (
            searchForm
          )}

          <Stack
            direction={'row'}
            sx={{
              alignItems: 'center',
              justifyContent: 'flex-end',
              flexShrink: 0
            }}
          >
            {isSearchCollapsed && <StarredPatientsList iconOnly />}
            <Box
              ref={notificationPopover.anchorRef}
              sx={{
                p: 0.25
              }}
            >
              <IconButton color="primary" onClick={notificationPopover.handleOpen}>
                <Badge badgeContent={unreadNotificationCount} color="error" max={10} invisible={notificationPopover.open}>
                  <NotificationsIcon />
                </Badge>
              </IconButton>
            </Box>
            <SecondaryMenu icon={<AppsIcon color={'primary'} />} />
          </Stack>

          {/* Below `md` the icon-only list above stands in for this; drawing both pushed the
              header past the right edge of a phone. */}
          {!isSearchCollapsed && <StarredPatientsList />}

          {/* The patient link bar's own controls, hoisted into the header so they sit with the
              rest of the global chrome.  They read the same store the bar does, and only mean
              anything while a patient is open -- hence the gate. */}
          {hasPatientBar && <PatientNavBarControls />}
        </Toolbar>

        {isSearchCollapsed && (
          <Collapse in={mobileSearchOpen} unmountOnExit>
            <Box
              sx={{
                bgcolor: 'background.paper',
                borderTop: '1px solid',
                borderColor: 'divider',
                boxShadow: '0 8px 16px -4px rgba(0,0,0,0.15)',
                px: 2,
                pt: 1.5,
                pb: 2
              }}
            >
              <Stack
                direction="row"
                spacing={1}
                sx={{
                  alignItems: 'center'
                }}
              >
                <Box sx={{ flex: 1 }}>{searchForm}</Box>
                <IconButton size="small" onClick={() => setMobileSearchOpen(false)}>
                  <CloseIcon />
                </IconButton>
              </Stack>
            </Box>
          </Collapse>
        )}
      </AppBar>

      <NotificationsPopover
        anchorEl={notificationPopover.anchorRef.current}
        open={notificationPopover.open}
        onClose={notificationPopover.handleClose}
      />

      {rowInOtherCompany && (
        <SwitchCompanyDialog
          open
          title={`Sign in to ${rowInOtherCompany.relyingPartyName ?? 'another pharmacy'}`}
          message={switchPrompt(
            rowInOtherCompany.name ?? 'this patient',
            currentUser?.relyingParty?.name ?? 'your current pharmacy',
            rowInOtherCompany.relyingPartyName ?? 'another pharmacy'
          )}
          suggestedCompanyName={rowInOtherCompany.relyingPartyName ?? undefined}
          onGoBack={() => setRowInOtherCompany(null)}
          onSwitch={confirmCompanySwitch}
          sx={{
            '& .MuiBackdrop-root': {
              backdropFilter: 'blur(6px)',
              backgroundColor: '#000000c2'
            }
          }}
        />
      )}
    </>
  );
};

export default Navbar;
